import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";

import fleetCoreOrbits from "../../images/fleetcore-luminous-orbits.svg";
import fleetCoreVan from "../../images/fleetcore-commercial-van.webp";

import {
  ReactComponent as FleetCoreDesktopDashboard,
} from "../../images/fleetcore-intelligence-dashboard-desktop.svg";

import {
  ReactComponent as FleetCoreMobileDashboard,
} from "../../images/fleetcore-intelligence-dashboard-mobile.svg";

import "./FleetCoreSection.css";

const MOBILE_BREAKPOINT = "(max-width: 760px)";
const XLINK_NAMESPACE = "http://www.w3.org/1999/xlink";

function ArrowRight({ className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14" />
      <path d="m13 6 6 6-6 6" />
    </svg>
  );
}

function useMobileDashboard() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }

    return window.matchMedia(MOBILE_BREAKPOINT).matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_BREAKPOINT);

    const handleChange = (event) => {
      setIsMobile(event.matches);
    };

    setIsMobile(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);

      return () => {
        mediaQuery.removeEventListener("change", handleChange);
      };
    }

    mediaQuery.addListener(handleChange);

    return () => {
      mediaQuery.removeListener(handleChange);
    };
  }, []);

  return isMobile;
}

function FleetCoreDashboardArtwork() {
  const dashboardContainerRef = useRef(null);
  const isMobile = useMobileDashboard();

  const DashboardComponent = isMobile
    ? FleetCoreMobileDashboard
    : FleetCoreDesktopDashboard;

  useLayoutEffect(() => {
    const dashboardElement = dashboardContainerRef.current;

    if (!dashboardElement) {
      return;
    }

    /*
     * The supplied FleetCore SVGs contain an internal <image> element
     * pointing to "fleetcore-commercial-van.png".
     *
     * Because Create React App generates a production filename for imported
     * images, we replace that internal reference with the imported URL.
     */
    const vehicleImage = dashboardElement.querySelector("svg image");

    if (!vehicleImage) {
      return;
    }

    vehicleImage.setAttribute("href", fleetCoreVan);
    vehicleImage.setAttributeNS(
      XLINK_NAMESPACE,
      "xlink:href",
      fleetCoreVan
    );
  }, [isMobile]);

  return (
    <div
      ref={dashboardContainerRef}
      className="fleetcore-section__picture"
    >
      <DashboardComponent
        className="fleetcore-section__dashboard"
        preserveAspectRatio={
          isMobile ? "none" : "xMidYMid meet"
        }
        role="img"
        aria-label="FleetCore intelligence dashboard showing a verified driver, vehicle mileage, refuelling evidence, 98 percent of fuel accounted for and no detected fuel anomaly"
      />
    </div>
  );
}

export default function FleetCoreSection() {
  return (
    <section
      id="fleetcore"
      className="fleetcore-section"
      aria-labelledby="fleetcore-title"
    >
      <img
        className="fleetcore-section__orbits"
        src={fleetCoreOrbits}
        alt=""
        aria-hidden="true"
      />

      <span
        className="fleetcore-section__blue-glow"
        aria-hidden="true"
      />

      <span
        className="fleetcore-section__pink-glow"
        aria-hidden="true"
      />

      <div className="fleetcore-section__inner">
        <div className="fleetcore-section__visual">
          <FleetCoreDashboardArtwork />
        </div>

        <div className="fleetcore-section__copy">
          <p className="fleetcore-section__eyebrow">
            FleetCore
          </p>

          <h2 id="fleetcore-title">
            Know every litre.
            <br />
            Account for every
            <br />
            kilometre.
          </h2>

          <p className="fleetcore-section__body">
            FleetCore connects verified drivers, vehicle condition,
            mileage and refuelling into one intelligent audit
            trail—helping fleets detect fuel fraud and protect every
            asset.
          </p>

          <Link
            className="fleetcore-section__button"
            to="/fleetcore"
            aria-label="Explore FleetCore"
          >
            <span>Explore FleetCore</span>
            <ArrowRight />
          </Link>
        </div>
      </div>
    </section>
  );
}
